<template>
  <hit-modal
    v-if="open"
    :title="$t('hit-admin.notifications.recipients-modal-title')"
    prefix-icon="group"
    popup-size="large"
    @cancel="$emit('close')"
  >
    <div v-if="loading" class="p-4 text-slate-500">
      {{ $t('hit-admin.notifications.recipients-loading') }}
    </div>
    <div v-else-if="error" class="p-4 text-red-700">
      {{ error }}
    </div>
    <template v-else>
      <div v-if="truncated" class="truncated-warn mb-3">
        {{ $t('hit-admin.notifications.recipients-truncated', {limit}) }}
      </div>
      <div class="summary mb-3 text-sm text-slate-600">
        {{
          $t('hit-admin.notifications.recipients-summary', {
            count: rows.length,
            users: uniqueUserCount,
          })
        }}
      </div>
      <div class="table-wrap">
        <table class="recipients-table">
          <thead>
            <tr>
              <th>{{ $t('hit-admin.notifications.col-name') }}</th>
              <th>{{ $t('hit-admin.notifications.col-email') }}</th>
              <th>{{ $t('hit-admin.notifications.col-company') }}</th>
              <th>{{ $t('hit-admin.notifications.col-company-status') }}</th>
              <th>{{ $t('hit-admin.notifications.col-admin') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, idx) in rows"
              :key="`${row.user_id}-${row.company_id}-${idx}`"
            >
              <td>{{ row.name }}</td>
              <td>{{ row.email || '—' }}</td>
              <td>{{ row.company_designation || '—' }}</td>
              <td>{{ row.company_verification_status || '—' }}</td>
              <td>
                {{
                  row.admin
                    ? $t('hit-admin.notifications.yes')
                    : $t('hit-admin.notifications.no')
                }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
    <div class="flex justify-end mt-4">
      <hit-button
        :label="$t('hit-admin.notifications.close')"
        color="button"
        @click="$emit('close')"
      />
    </div>
  </hit-modal>
</template>

<script>
import {defineComponent} from 'vue';
import {HitModal, HitButton} from '@hit/components';
import {adminApi} from '@admin/services/adminApi';

export default defineComponent({
  name: 'NotificationRecipientsModal',
  components: {HitModal, HitButton},
  props: {
    open: {type: Boolean, default: false},
    previewParams: {type: Object, default: null},
  },
  emits: ['close'],
  data() {
    return {
      loading: false,
      error: null,
      rows: [],
      truncated: false,
      limit: 5000,
    };
  },
  computed: {
    uniqueUserCount() {
      return new Set(this.rows.map((r) => r.user_id)).size;
    },
  },
  watch: {
    open(isOpen) {
      if (isOpen) {
        this.loadRecipients();
      } else {
        this.rows = [];
        this.error = null;
      }
    },
  },
  methods: {
    async loadRecipients() {
      if (!this.previewParams) return;
      this.loading = true;
      this.error = null;
      try {
        const res = await adminApi.previewNotificationRecipients(
          this.previewParams
        );
        this.rows = res?.rows || [];
        this.truncated = !!res?.truncated;
      } catch (e) {
        this.error = e?.message || String(e);
        this.rows = [];
      } finally {
        this.loading = false;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.table-wrap {
  max-height: min(60vh, 28rem);
  overflow: auto;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 0.5rem;
}

.recipients-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
  th,
  td {
    padding: 0.45rem 0.65rem;
    text-align: left;
    border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  }
  th {
    position: sticky;
    top: 0;
    background: #f8fafc;
    font-weight: 600;
    color: #475569;
    z-index: 1;
  }
  tbody tr:hover {
    background: rgba(0, 153, 124, 0.04);
  }
}

.truncated-warn {
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  background: rgba(255, 107, 0, 0.1);
  color: #b45309;
  font-size: 0.875rem;
}
</style>
